import React, { useState, useEffect } from 'react';
import { faqAPI } from '../services/api';

/**
 * HierarchicalFAQSelector Component
 * 
 * Provides a three-level hierarchical dropdown selector for FAQ categorization.
 * Cascading dropdowns that load options based on parent selections.
 * 
 * @param {Object} props - Component props
 * @param {string} props.selectedDomain - Currently selected domain
 * @param {string} props.selectedSubCatHead - Currently selected subcategory head
 * @param {string} props.selectedSubCategory - Currently selected subcategory
 * @param {Function} props.onDomainChange - Handler for domain change
 * @param {Function} props.onSubCatHeadChange - Handler for subcategory head change
 * @param {Function} props.onSubCategoryChange - Handler for subcategory change
 * @param {boolean} props.allowNew - Whether to allow entering new values (default: false)
 * @returns {JSX.Element} The hierarchical FAQ selector component
 */
const HierarchicalFAQSelector = ({ 
  selectedDomain,
  selectedSubCatHead,
  selectedSubCategory,
  onDomainChange,
  onSubCatHeadChange,
  onSubCategoryChange,
  allowNew = false
}) => {
  const [domains, setDomains] = useState([]);
  const [subCatHeads, setSubCatHeads] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load domains on component mount
  useEffect(() => {
    loadDomains();
  }, []);

  // Load subcategory heads when domain changes
  useEffect(() => {
    if (selectedDomain) {
      loadSubCatHeads(selectedDomain);
    } else {
      setSubCatHeads([]);
      setSubCategories([]);
    }
  }, [selectedDomain]);

  // Load subcategories when subcategory head changes
  useEffect(() => {
    if (selectedDomain && selectedSubCatHead) {
      loadSubCategories(selectedDomain, selectedSubCatHead);
    } else {
      setSubCategories([]);
    }
  }, [selectedDomain, selectedSubCatHead]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await faqAPI.getFAQDomains();
      setDomains(response || []);
    } catch (error) {
      console.error('Error loading domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubCatHeads = async (domain) => {
    try {
      setLoading(true);
      const response = await faqAPI.getFAQSubCatHeads(domain);
      setSubCatHeads(response || []);
    } catch (error) {
      console.error('Error loading subcategory heads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubCategories = async (domain, subCatHead) => {
    try {
      setLoading(true);
      const response = await faqAPI.getFAQSubCategories(domain, subCatHead);
      setSubCategories(response || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Domain Dropdown/Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Domain * <span className="text-xs font-normal text-gray-500">(Top-level category)</span>
        </label>
        {allowNew ? (
          <div className="relative">
            <input
              type="text"
              value={selectedDomain}
              onChange={(e) => {
                onDomainChange(e.target.value);
                onSubCatHeadChange('');
                onSubCategoryChange('');
              }}
              list="domains-list"
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Type new or select existing..."
              required
              disabled={loading}
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
              ✏️
            </span>
          </div>
        ) : (
          <select
            value={selectedDomain}
            onChange={(e) => {
              onDomainChange(e.target.value);
              onSubCatHeadChange('');
              onSubCategoryChange('');
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            required
            disabled={loading}
          >
            <option value="">Select domain...</option>
            {domains.map((domain) => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        )}
        {allowNew && (
          <>
            <datalist id="domains-list">
              {domains.map((domain) => (
                <option key={domain} value={domain} />
              ))}
            </datalist>
            {domains.length > 0 && (
              <p className="text-xs text-red-600 mt-1">
                💡 Type to create new or select from {domains.length} existing domain{domains.length !== 1 ? 's' : ''}
              </p>
            )}
            {domains.length === 0 && (
              <p className="text-xs text-green-600 mt-1">
                💡 Type to create your first domain (e.g., "Student Services")
              </p>
            )}
          </>
        )}
        {!allowNew && (
          <p className="text-xs text-gray-500 mt-1">
            Top-level categorization (required)
          </p>
        )}
      </div>

      {/* SubCatHead Dropdown/Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          SubCatHead <span className="text-xs font-normal text-gray-500">(First subcategory)</span>
        </label>
        {allowNew ? (
          <div className="relative">
            <input
              type="text"
              value={selectedSubCatHead}
              onChange={(e) => {
                onSubCatHeadChange(e.target.value);
                onSubCategoryChange('');
              }}
              list="sub-cat-heads-list"
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Type new or select existing..."
              disabled={!selectedDomain || loading}
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
              ✏️
            </span>
          </div>
        ) : (
          <select
            value={selectedSubCatHead}
            onChange={(e) => {
              onSubCatHeadChange(e.target.value);
              onSubCategoryChange('');
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={!selectedDomain || loading}
          >
            <option value="">Select subcategory head...</option>
            {subCatHeads.map((sch) => (
              <option key={sch} value={sch}>
                {sch}
              </option>
            ))}
          </select>
        )}
        {allowNew && (
          <>
            <datalist id="sub-cat-heads-list">
              {subCatHeads.map((sch) => (
                <option key={sch} value={sch} />
              ))}
            </datalist>
            {selectedDomain && subCatHeads.length > 0 && (
              <p className="text-xs text-red-600 mt-1">
                💡 Type to create new or select from {subCatHeads.length} existing
              </p>
            )}
            {selectedDomain && subCatHeads.length === 0 && (
              <p className="text-xs text-green-600 mt-1">
                💡 Type to create (e.g., "Admissions", "Registration")
              </p>
            )}
          </>
        )}
        {!allowNew && (
          <p className="text-xs text-gray-500 mt-1">
            First level subcategory (optional)
          </p>
        )}
      </div>

      {/* SubCategory Dropdown/Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          SubCategory <span className="text-xs font-normal text-gray-500">(Second subcategory)</span>
        </label>
        {allowNew ? (
          <div className="relative">
            <input
              type="text"
              value={selectedSubCategory}
              onChange={(e) => onSubCategoryChange(e.target.value)}
              list="sub-categories-list"
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Type new or select existing..."
              disabled={!selectedDomain || loading}
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
              ✏️
            </span>
          </div>
        ) : (
          <select
            value={selectedSubCategory}
            onChange={(e) => onSubCategoryChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={!selectedDomain || loading}
          >
            <option value="">Select subcategory...</option>
            {subCategories.map((sc) => (
              <option key={sc} value={sc}>
                {sc}
              </option>
            ))}
          </select>
        )}
        {allowNew && (
          <>
            <datalist id="sub-categories-list">
              {subCategories.map((sc) => (
                <option key={sc} value={sc} />
              ))}
            </datalist>
            {selectedDomain && subCategories.length > 0 && (
              <p className="text-xs text-red-600 mt-1">
                💡 Type to create new or select from {subCategories.length} existing
              </p>
            )}
            {selectedDomain && subCategories.length === 0 && (
              <p className="text-xs text-green-600 mt-1">
                💡 Type to create (e.g., "Undergraduate", "Graduate")
              </p>
            )}
          </>
        )}
        {!allowNew && (
          <p className="text-xs text-gray-500 mt-1">
            Second level subcategory (optional)
          </p>
        )}
      </div>
    </div>
  );
};

export default HierarchicalFAQSelector;
